import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import {
  ThemeProvider,
  CssBaseline,
  createTheme,
  alpha
} from '@mui/material';
const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb', // Vibrant Blue
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0d9488', // Elegant Teal
      light: '#2dd4bf',
      dark: '#0f766e',
      contrastText: '#ffffff',
    },
    tertiary: {
      main: '#7c3aed', // Purple accent
      light: '#a78bfa',
      dark: '#5b21b6',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
      light: '#f1f5f9',
    },
    text: {
      primary: '#1e293b', // Slate 800
      secondary: '#64748b', // Slate 600
      disabled: '#94a3b8', // Slate 400
    },
    gradient: {
      primary: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      secondary: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
      accent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
    },
  },

  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      background: 'linear-gradient(135deg, #1e293b 0%, #2563eb 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h2: {
      fontWeight: 800,
      fontSize: '2.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      color: '#1e293b',
    },
    h3: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.3,
      color: '#1e293b',
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.875rem',
      lineHeight: 1.4,
      color: '#1e293b',
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.5,
      color: '#1e293b',
    },
    h6: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      color: '#1e293b',
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      color: '#475569',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#64748b',
    },
    button: {
      fontWeight: 700,
      fontSize: '0.9375rem',
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontWeight: 500,
      fontSize: '0.75rem',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },

  shape: {
    borderRadius: 7,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(30, 41, 59, 0.05)',
    '0px 4px 6px -2px rgba(30, 41, 59, 0.05), 0px 2px 4px -2px rgba(30, 41, 59, 0.05)',
    '0px 12px 16px -4px rgba(30, 41, 59, 0.08), 0px 4px 6px -2px rgba(30, 41, 59, 0.05)',
    '0px 20px 24px -4px rgba(30, 41, 59, 0.08), 0px 8px 8px -4px rgba(30, 41, 59, 0.03)',
    '0px 24px 48px -12px rgba(30, 41, 59, 0.12)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 28px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px 0 rgba(37, 99, 235, 0.45)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: alpha('#2563eb', 0.04),
          },
        },
      },
      variants: [
        {
          props: { variant: 'gradient' },
          style: {
            background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
            color: 'white',
            boxShadow: '0 4px 14px 0 rgba(13, 148, 136, 0.39)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
              boxShadow: '0 8px 25px 0 rgba(13, 148, 136, 0.45)',
            },
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '24px 0 0 24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 8px',
          '&:hover': {
            background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          overflowY: 'auto',
        },

        /* Chrome, Edge, Safari */
        '::-webkit-scrollbar': {
          width: '10px',
        },
        '::-webkit-scrollbar-track': {
          background: '#f1f5f9',
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: '#94a3b8',
          borderRadius: '8px',
          border: '2px solid #f1f5f9',
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#64748b',
        },

        /* Firefox */
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#94a3b8 #f1f5f9',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
     <ThemeProvider theme={theme}>
        <CssBaseline />
    <App />
      </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
